import { toEnglishDigits } from 'utilities/helper/toEnglishNumber'

/**
 * Ordering and name-search for a list of funds — shared by the home page's
 * «صندوق‌هایی که مدیر آن هستید» rows and by the صندوق‌ها tab's cards.
 *
 * The order a user drags into is **client-only** on purpose: it lives in this
 * browser's localStorage, not on the fund. That is what makes the feature cheap
 * enough to ship for feedback — no endpoint, no migration, no per-poolship
 * column — and it is also why the merge below has to be defensive. The stored
 * value is a list of ids from an arbitrarily old session: funds in it may have
 * been left, and funds not in it may have been joined since.
 *
 * Each list owns its own storage key. The two lists are *not* the same list —
 * home shows only the funds you manage, the tab shows every fund you are in —
 * so one shared key would let home's save (managed ids only) erase the tab's
 * memory of every fund the user is merely a member of.
 */

/** The one thing this module needs of a fund: something to key the order by. */
interface Identified {
    id: number
}

export function readSavedOrder(key: string): number[] {
    try {
        const raw = window.localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : null
        if (!Array.isArray(parsed)) return []
        return parsed.filter((id): id is number => typeof id === 'number')
    } catch {
        return []
    }
}

export function saveOrder(key: string, ids: number[]): void {
    try {
        window.localStorage.setItem(key, JSON.stringify(ids))
    } catch {
        // Private-mode Safari and a full quota both throw here. Losing the
        // preference is not worth blowing up the screen over.
    }
}

/**
 * Merge the saved order with the funds the API actually returned.
 *
 * `baseOrder` is the ranking to use before anyone has dragged anything, and the
 * tie-breaker for funds the saved order has never seen. It defaults to "leave
 * the API's own order alone", which is what the صندوق‌ها tab wants; home passes
 * its own ranking.
 *
 * Funds the saved order has never seen go **first**. That looks backwards until
 * you remember what the alternative costs: home collapses to three rows, so a
 * fund joined today and appended to the bottom of a hand-sorted list of nine
 * would be invisible behind «۶ صندوق دیگر»; on the tab it would be a full
 * screen of scrolling away. Putting it on top costs one drag to undo and cannot
 * hide anything.
 *
 * Ids in the saved order that no longer resolve to a fund are dropped, and the
 * caller rewrites the whole list on the next drag, so stale ids do not
 * accumulate.
 */
export function applySavedOrder<T extends Identified>(
    funds: T[],
    savedIds: number[],
    baseOrder: (funds: T[]) => T[] = (input) => input
): T[] {
    const ranked = baseOrder(funds)
    if (savedIds.length === 0) return ranked

    const position = new Map(savedIds.map((id, index) => [id, index]))
    const known: T[] = []
    const fresh: T[] = []
    ranked.forEach((item) => (position.has(item.id) ? known : fresh).push(item))
    known.sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0))
    return [...fresh, ...known]
}

/**
 * Fold away the spelling differences that make Persian search feel broken:
 * Arabic yeh/kaf typed by iOS keyboards, ZWNJ inside compound words
 * («قرض‌الحسنه» vs «قرض الحسنه»), alef with hamza, heh-yeh, tatweel, and
 * Persian/Arabic digits.
 */
export function normalizeFa(text: string): string {
    return toEnglishDigits(text)
        .replace(/[يى]/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/[أإآ]/g, 'ا')
        .replace(/[ۀة]/g, 'ه')
        .replace(/[ً-ْـ]/g, '') // harakat + tatweel
        .replace(/[‌‏‎]/g, ' ') // ZWNJ and bidi marks
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * Every whitespace-separated token of the query must appear somewhere in the
 * name, in any order — so «خانوادگی قرض» finds «صندوق قرض‌الحسنه خانوادگی».
 */
export function matchesName(name: string | undefined, query: string): boolean {
    const tokens = normalizeFa(query).split(' ').filter(Boolean)
    if (tokens.length === 0) return true
    const haystack = normalizeFa(name ?? '')
    return tokens.every((token) => haystack.includes(token))
}
